import React, { ChangeEvent, ReactNode } from 'react'
import { Box, ButtonBase, Checkbox, InputAdornment, OutlinedInput, Radio, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'
import { C, focusRing } from './tokens'

// Regra do tema do admin: TextField herda filled + fullWidth (e 100% abaixo de sm). Campo compacto usa
// OutlinedInput solto, dentro de uma caixa de largura pelo conteúdo. Fonte 16 px no celular evita o zoom do iOS.

export const inputSx = (invalid?: boolean): SxProps<Theme> => ({
  bgcolor: '#fff',
  color: C.ink,
  fontSize: { xs: 16, sm: 15 },
  fontVariantNumeric: 'tabular-nums',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: invalid ? C.red : C.inputBorder },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: invalid ? C.red : C.ink },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: invalid ? C.red : C.violet, borderWidth: 2 },
  '& input::placeholder, & textarea::placeholder': { color: C.quiet, opacity: 1 },
})

interface LabeledInputProps {
  id: string
  label: ReactNode
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** Largura máxima em px. O campo ocupa a linha toda quando ela é mais estreita (celular, tablet com menu aberto). */
  width?: number
  invalid?: boolean
  describedBy?: string
  prefix?: ReactNode
  suffix?: ReactNode
  type?: string
  inputMode?: 'numeric' | 'text' | 'decimal'
  placeholder?: string
  multiline?: boolean
  minRows?: number
  min?: string
  max?: string
  maxLength?: number
  center?: boolean
  /** Rótulo só para leitor de tela (quando o texto visível já está ao lado). */
  hideLabel?: boolean
  disabled?: boolean
  autoFocus?: boolean
  right?: ReactNode
  /** Frase de apoio abaixo do campo (evita um <Box> + <Hint> em volta a cada uso). */
  hint?: ReactNode
}

/** Campo com rótulo em cima (12,5 px) e largura pelo conteúdo. */
export const LabeledInput: React.FC<LabeledInputProps> = ({
  id, label, value, onChange, width = 320, invalid, describedBy, prefix, suffix, type, inputMode, placeholder,
  multiline, minRows, min, max, maxLength, center, hideLabel, disabled, autoFocus, right, hint,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', maxWidth: width, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Box
        component="label"
        htmlFor={id}
        sx={hideLabel
          ? visuallyHidden
          : { fontSize: 12.5, fontWeight: 500, color: C.muted }}
      >
        {label}
      </Box>
      {right}
    </Box>
    <OutlinedInput
      id={id}
      value={value}
      onChange={onChange}
      size="small"
      fullWidth
      error={invalid}
      disabled={disabled}
      autoFocus={autoFocus}
      multiline={multiline}
      minRows={minRows}
      type={type}
      placeholder={placeholder}
      startAdornment={prefix ? <InputAdornment position="start" sx={{ '& p': { fontSize: 14, color: C.muted } }}>{prefix}</InputAdornment> : undefined}
      endAdornment={suffix ? <InputAdornment position="end" sx={{ '& p': { fontSize: 14, color: C.muted } }}>{suffix}</InputAdornment> : undefined}
      slotProps={{
        input: {
          inputMode, min, max, maxLength, autoComplete: 'off',
          'aria-invalid': invalid || undefined, 'aria-describedby': describedBy,
          style: center ? { textAlign: 'center' } : undefined,
        } as never,
      }}
      sx={inputSx(invalid)}
    />
    {hint ? <Box component="span" sx={{ fontSize: 12.5, color: C.quiet, lineHeight: 1.45 }}>{hint}</Box> : null}
  </Box>
)

/** Sombra ao passar o mouse num cartão clicável. Fora do ternário: o plugin de i18n embrulha literal com espaço dentro de condicional. */
export const cardHoverSx = { boxShadow: '0 6px 18px rgba(30,41,59,.10)' } as const

interface RadioCardProps {
  name: string
  checked: boolean
  onChange: () => void
  title: ReactNode
  desc?: ReactNode
  disabled?: boolean
  sx?: SxProps<Theme>
}

/** Opção em cartão: o <label> inteiro é o alvo (≥ 44 px), com <input type="radio"> de verdade. */
export const RadioCard: React.FC<RadioCardProps> = ({ name, checked, onChange, title, desc, disabled, sx }) => (
  <Box
    component="label"
    sx={{
      display: 'flex', gap: 1.5, alignItems: 'flex-start', p: '14px 16px', minHeight: 44, boxSizing: 'border-box', borderRadius: '12px',
      border: `1.5px solid ${checked ? C.violet : C.radioBorder}`, bgcolor: checked ? C.violetSoft : '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      transition: 'border-color .15s ease, background-color .15s ease, box-shadow .15s ease',
      '&:hover': disabled ? undefined : cardHoverSx,
      '&:has(input:focus-visible)': focusRing,
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      ...(sx as object),
    }}
  >
    <Radio
      name={name}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      disableRipple
      sx={{ p: 0, mt: '1px', color: C.inputBorder, '&.Mui-checked': { color: C.violet }, '& .MuiSvgIcon-root': { fontSize: 22 } }}
    />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, minWidth: 0 }}>
      <Box component="span" sx={{ fontSize: 15, fontWeight: 500, color: C.ink, lineHeight: 1.35 }}>{title}</Box>
      {desc ? <Box component="span" sx={{ fontSize: 13, color: C.muted, lineHeight: 1.4 }}>{desc}</Box> : null}
    </Box>
  </Box>
)

/** Grupo de opções em cartão (lado a lado quando cabe, empilhado no celular). */
export const RadioRow: React.FC<{ id?: string; labelledBy: string; children: ReactNode; describedBy?: string }> = ({ id, labelledBy, children, describedBy }) => (
  <Box
    id={id}
    role="radiogroup"
    aria-labelledby={labelledBy}
    aria-describedby={describedBy}
    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, '& > label': { flex: '1 1 240px', maxWidth: { sm: 420 } } }}
  >
    {children}
  </Box>
)

/** Atalho em forma de pílula (valor rápido, período, dia da semana). */
export const Pill: React.FC<{ on: boolean; onClick: () => void; children: ReactNode; sx?: SxProps<Theme>; ariaLabel?: string }> = ({ on, onClick, children, sx, ariaLabel }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={on}
    aria-label={ariaLabel}
    focusRipple={false}
    sx={{
      height: { xs: 40, sm: 36 }, px: 1.75, borderRadius: 999, border: `1px solid ${on ? C.violet : C.inputBorder}`,
      bgcolor: on ? C.violet : '#fff', color: on ? '#fff' : C.ink, fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums', '&.Mui-focusVisible': focusRing, ...(sx as object),
    }}
  >
    {children}
  </ButtonBase>
)

/** Alternador de 2 ou 3 opções (Grátis / Gasta pontos, a cada / no total, Com pontos / Sem pontos). */
// Cor do botão marcado. O verde da marca leva texto escuro: branco sobre #79CF62 fica em
// ~1,8:1. Fora do JSX porque o plugin de i18n embrulha literal dentro de ternário.
const SEG_SELECTED_INK = { bgcolor: C.ink, color: '#fff', fontWeight: 700, '&:hover': { bgcolor: '#334155' } } as const
const SEG_SELECTED_BRAND = { bgcolor: C.green, color: C.ink, fontWeight: 700, '&:hover': { bgcolor: C.greenHover } } as const

export function Seg<T extends string>({ value, onChange, options, label, height = 40, fill, tone = 'ink' }: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: ReactNode }[]
  label: string
  height?: number
  fill?: boolean
  /** Cor do marcado: 'ink' (padrão, o do cadastro de desconto) ou 'brand' (verde do admin). */
  tone?: 'ink' | 'brand'
}) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, v) => { if (v != null) onChange(v as T) }}
      aria-label={label}
      sx={{
        height: { xs: height + 2, sm: height }, border: `1px solid ${C.inputBorder}`, borderRadius: '8px', bgcolor: '#fff', width: fill ? '100%' : 'auto',
        '& .MuiToggleButtonGroup-grouped': {
          border: 0, borderRadius: 0, textTransform: 'none', color: C.ink, fontSize: 14, fontWeight: 500, px: 1.75, flex: fill ? 1 : 'none', whiteSpace: 'nowrap',
          '&:first-of-type': { borderRadius: '7px 0 0 7px' },
          '&:last-of-type': { borderRadius: '0 7px 7px 0' },
          '&:not(:first-of-type)': { borderLeft: `1px solid ${C.inputBorder}`, ml: 0 },
          '&.Mui-selected': tone === 'brand' ? SEG_SELECTED_BRAND : SEG_SELECTED_INK,
          '&.Mui-focusVisible': { outline: `2px solid ${C.violet}`, outlineOffset: '-3px', position: 'relative', zIndex: 1 },
        },
      }}
    >
      {options.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  )
}

/** Caixa de seleção com texto (linha inteira clicável, ≥ 40 px). */
// Sem descrição, a caixa e o texto ficam no meio da linha de 40 px (no topo sobra um vão embaixo).
const ALIGN_TOP = 'flex-start'
const ALIGN_MIDDLE = 'center'
const CURSOR_ON = 'pointer'
const CURSOR_OFF = 'not-allowed'

export const CheckRow: React.FC<{ id?: string; checked: boolean; onChange: () => void; children: ReactNode; desc?: ReactNode; invalid?: boolean; describedBy?: string; disabled?: boolean; sx?: SxProps<Theme> }> = ({ id, checked, onChange, children, desc, invalid, describedBy, disabled, sx }) => (
  <Box component="label" sx={{ display: 'flex', gap: 1.25, alignItems: desc ? ALIGN_TOP : ALIGN_MIDDLE, cursor: disabled ? CURSOR_OFF : CURSOR_ON, opacity: disabled ? 0.6 : 1, minHeight: 40, py: 0.5, '&:has(input:focus-visible)': { ...focusRing, borderRadius: '6px' }, ...(sx as object) }}>
    <Checkbox
      id={id}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      disableRipple
      slotProps={{ input: { 'aria-invalid': invalid || undefined, 'aria-describedby': describedBy } as never }}
      sx={{ p: 0, mt: '1px', color: C.inputBorder, '&.Mui-checked': { color: C.violet }, '& .MuiSvgIcon-root': { fontSize: 22 } }}
    />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      <Box component="span" sx={{ fontSize: 14.5, fontWeight: 500, color: C.ink, lineHeight: 1.4 }}>{children}</Box>
      {desc ? <Box component="span" sx={{ fontSize: 13, color: C.muted, lineHeight: 1.4 }}>{desc}</Box> : null}
    </Box>
  </Box>
)

/** Interruptor com explicação, num cartão (Pontuar ao usar desconto). */
export const SwitchCard: React.FC<{ id: string; checked: boolean; onChange: () => void; title: ReactNode; desc: ReactNode }> = ({ id, checked, onChange, title, desc }) => (
  <Box
    component="label"
    htmlFor={id}
    sx={{
      display: 'flex', gap: 1.5, alignItems: 'flex-start', cursor: 'pointer', p: '14px 16px', border: `1px solid ${C.line}`, borderRadius: '12px', maxWidth: 640,
      '&:has(input:focus-visible)': focusRing,
    }}
  >
    <Switch
      id={id}
      checked={checked}
      onChange={onChange}
      sx={{ flex: 'none', mt: '-6px', ml: '-6px', '& .MuiSwitch-switchBase.Mui-checked': { color: C.violet }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.violet } }}
    />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, minWidth: 0 }}>
      <Box component="span" sx={{ fontSize: 15, fontWeight: 500, color: C.ink }}>{title}</Box>
      <Box component="span" sx={{ fontSize: 13, color: C.muted, lineHeight: 1.45 }}>{desc}</Box>
    </Box>
  </Box>
)

/** Botão de texto (violeta) para ações pequenas: Alterar, Remover, Limpar... Alvo mínimo de 40 px no celular. */
export const TextButton: React.FC<{ id?: string; onClick: () => void; children: ReactNode; disabled?: boolean; icon?: ReactNode; color?: string; ariaLabel?: string; sx?: SxProps<Theme>; underline?: boolean; expanded?: boolean; controls?: string }> = ({ id, onClick, children, disabled, icon, color = C.violet, ariaLabel, sx, underline, expanded, controls }) => (
  <ButtonBase
    id={id}
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    aria-expanded={expanded}
    aria-controls={controls}
    focusRipple={false}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, borderRadius: '6px', minHeight: { xs: 40, sm: 36 }, color, fontSize: 14, fontWeight: 500,
      textDecoration: underline ? 'underline' : 'none', '&:hover': { bgcolor: 'rgba(68,51,180,.07)' }, '&.Mui-disabled': { opacity: 0.6 },
      '&.Mui-focusVisible': focusRing, '& .MuiSvgIcon-root': { fontSize: 18 }, ...(sx as object),
    }}
  >
    {icon}
    {children}
  </ButtonBase>
)

const buttonBase = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1, borderRadius: '8px', fontSize: 15, whiteSpace: 'nowrap',
  '&.Mui-focusVisible': focusRing, '& .MuiSvgIcon-root': { fontSize: 20 },
} as const

interface ButtonProps {
  onClick?: () => void
  children: ReactNode
  disabled?: boolean
  icon?: ReactNode
  endIcon?: ReactNode
  sx?: SxProps<Theme>
  /** Para virar link do router: `component={Link} extra={{ to: '/discounts' }}`. */
  component?: React.ElementType
  extra?: Record<string, unknown>
  autoFocus?: boolean
}

/** Botão principal: verde da marca com texto escuro (verde com texto branco não passa no contraste). */
export const PrimaryButton: React.FC<ButtonProps & { busy?: boolean }> = ({ onClick, children, disabled, busy, icon, endIcon, sx, component, extra, autoFocus }) => (
  <ButtonBase
    {...(component ? { component } : {})}
    {...(extra as object)}
    onClick={onClick}
    disabled={disabled}
    autoFocus={autoFocus}
    aria-busy={busy || undefined}
    focusRipple={false}
    sx={{
      ...buttonBase, height: 44, px: 2.5, bgcolor: C.green, color: C.ink, fontWeight: 700, textDecoration: 'none', transition: 'background-color .15s ease, box-shadow .15s ease',
      '&:hover': { bgcolor: C.greenHover, boxShadow: '0 6px 16px rgba(39,80,48,.18)' }, '&.Mui-disabled': { opacity: 0.7, cursor: 'progress' }, ...(sx as object),
    }}
  >
    {icon}
    {children}
    {endIcon}
  </ButtonBase>
)

/** Botão secundário: contorno cinza, texto escuro. */
export const OutlineButton: React.FC<ButtonProps> = ({ onClick, children, disabled, icon, sx, component, extra, autoFocus }) => (
  <ButtonBase
    {...(component ? { component } : {})}
    {...(extra as object)}
    onClick={onClick}
    disabled={disabled}
    autoFocus={autoFocus}
    focusRipple={false}
    sx={{
      ...buttonBase, height: 44, px: 2, border: `1px solid ${C.inputBorder}`, bgcolor: '#fff', color: C.ink, fontWeight: 500, textDecoration: 'none',
      '&:hover': { bgcolor: '#f1f5f9' }, '&.Mui-disabled': { opacity: 0.6 }, '& .MuiSvgIcon-root': { fontSize: 20, color: '#334155' }, ...(sx as object),
    }}
  >
    {icon}
    {children}
  </ButtonBase>
)

/** Abas pequenas em trilho cinza (Na bomba / Notificação, Com pontos / Sem pontos). */
export function SoftSeg<T extends string>({ value, onChange, options, label, height = 36 }: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: ReactNode }[]
  label: string
  height?: number
}) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', p: '3px', borderRadius: '10px', bgcolor: '#f1f5f9', gap: '3px' }}>
      {options.map(o => {
        const on = o.value === value
        return (
          <ButtonBase
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={on}
            focusRipple={false}
            sx={{
              flex: 1, height: { xs: Math.max(height, 40), sm: height }, borderRadius: '8px', fontSize: 13.5, color: C.ink, fontWeight: on ? 700 : 500,
              bgcolor: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 3px rgba(15,23,42,.12)' : 'none', '&.Mui-focusVisible': focusRing,
            }}
          >
            {o.label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}

interface FieldProps {
  id?: string
  label: ReactNode
  /** Frase de apoio abaixo do controle. */
  hint?: ReactNode
  /** Largura máxima em px; o campo ocupa a linha toda quando ela é mais estreita. */
  width?: number
  children: ReactNode
  sx?: SxProps<Theme>
}

/**
 * Rótulo acima + um controle qualquer (Select, Autocomplete, CurrencyInput), nas mesmas
 * métricas do LabeledInput. Existe porque o LabeledInput só embrulha OutlinedInput: para
 * um Autocomplete do MUI, o rótulo flutuante dentro do contorno quebra o alinhamento da
 * linha. O controle de dentro vai SEM `label` — quem rotula é o <label> daqui.
 */
export const Field: React.FC<FieldProps> = ({ id, label, hint, width = 320, children, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', maxWidth: width, minWidth: 0, ...(sx as object) }}>
    <Box component="label" htmlFor={id} sx={{ fontSize: 12.5, fontWeight: 500, color: C.muted }}>{label}</Box>
    {children}
    {hint ? <Box component="span" sx={{ fontSize: 12.5, color: C.quiet, lineHeight: 1.45 }}>{hint}</Box> : null}
  </Box>
)
